import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, Bed, Calendar, DollarSign, TrendingUp,
  Activity, AlertCircle, CheckCircle, Clock, Settings,
  BarChart3, MessageSquare, Bot, Bell,
  Home, Package, UserCog, FileText, LogOut, Cpu, ChevronDown
} from 'lucide-react';
import {
  getDashboard,
  getBookings,
  checkInBooking,
  checkOutBooking,
  cancelBookingLifecycle,
  getNotifications,
  markAllNotificationsRead,
} from '../services/managerAPI';
import HotelOverview from '../components/manager/HotelOverview';
import HotelConfiguration from '../components/manager/HotelConfiguration';
import ManagerAgent from '../components/ManagerAgent';
import './ManagerDashboard.css';

const NAV_ITEMS = [
  { key: 'overview',   label: 'Overview',        Icon: Home,     route: null },
  { key: 'config',     label: 'Hotel Config',     Icon: Building2, route: null },
  { key: 'bookings',   label: 'Bookings',         Icon: Calendar, route: null },
  { key: 'rooms',      label: 'Rooms',            Icon: Bed,      route: '/manager/rooms' },
  { key: 'staff',      label: 'Staff',            Icon: UserCog,  route: '/manager/staff' },
  { key: 'inventory',  label: 'Inventory',        Icon: Package,  route: '/manager/inventory' },
  { key: 'iot',        label: 'IoT Devices',      Icon: Cpu,      route: '/manager/iot-devices' },
  { key: 'analytics',  label: 'Analytics',        Icon: BarChart3, route: null },
  { key: 'reports',    label: 'Reports',          Icon: FileText, route: null },
  { key: 'settings',   label: 'Settings',         Icon: Settings, route: null },
];

const ManagerDashboard = () => {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('overview');
  const [showAgentChat, setShowAgentChat] = useState(false);
  const [hotelData, setHotelData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dashboard stats
  const [stats, setStats] = useState({
    totalRooms: 0,
    occupiedRooms: 0,
    availableRooms: 0,
    checkInsToday: 0,
    checkOutsToday: 0,
    pendingBookings: 0,
    revenue: {
      today: 0,
      week: 0,
      month: 0
    },
    occupancyRate: 0,
    averageRating: 0,
    activeStaff: 0,
    pendingIssues: 0
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsError, setBookingsError] = useState('');
  const [bookingActionBusyId, setBookingActionBusyId] = useState(null);

  // ── Notifications ─────────────────────────────────────
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const notifPanelRef = useRef(null);

  useEffect(() => {
    loadDashboardData();
  }, [userProfile, navigate]);

  useEffect(() => {
    if (activeView === 'bookings') {
      loadBookings();
    }
  }, [activeView]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch real dashboard data from API
      const response = await getDashboard();
      
      if (response.success) {
        const { stats: apiStats, recentActivity, alerts: apiAlerts, hotel } = response.data;
        
        // Update stats from API
        setStats({
          totalRooms: apiStats.rooms.total,
          occupiedRooms: apiStats.rooms.occupied,
          availableRooms: apiStats.rooms.available,
          checkInsToday: apiStats.bookings.todaysCheckIns,
          checkOutsToday: apiStats.bookings.todaysCheckOuts,
          pendingBookings: apiStats.bookings.pending,
          revenue: {
            today: apiStats.revenue.daily,
            week: apiStats.revenue.daily * 7, // Approximate
            month: apiStats.revenue.monthly
          },
          occupancyRate: apiStats.rooms.occupancyRate,
          averageRating: 4.5, // TODO: Add to API
          activeStaff: apiStats.staff.onDuty,
          pendingIssues: apiAlerts.length
        });

        // Format recent activity
        setRecentActivities(recentActivity.map(activity => ({
          id: activity.id,
          type: activity.type,
          message: `${activity.title} - ${activity.description}`,
          time: new Date(activity.timestamp).toLocaleString(),
          status: activity.type === 'booking' ? 'success' : 'info'
        })));

        // Format alerts
        setAlerts(apiAlerts.map(alert => ({
          id: alert.id,
          type: alert.type,
          message: alert.message,
          priority: alert.severity
        })));

        setHotelData({
          hotelId: hotel.id,
          hotelName: hotel.name,
          location: 'Downtown, City Center', // TODO: Add to API
          rating: 4.5 // TODO: Add to API
        });
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      
      // Fall back to mock data if API fails
      setStats({
        totalRooms: 50,
        occupiedRooms: 35,
        availableRooms: 15,
        checkInsToday: 8,
        checkOutsToday: 5,
        pendingBookings: 12,
        revenue: {
          today: 4500,
          week: 28000,
          month: 125000
        },
        occupancyRate: 70,
        averageRating: 4.5,
        activeStaff: 24,
        pendingIssues: 3
      });

      setRecentActivities([
        { id: 1, type: 'booking', message: 'New booking for Room 205', time: '5 minutes ago', status: 'success' },
        { id: 2, type: 'checkin', message: 'Guest checked in to Room 101', time: '15 minutes ago', status: 'success' },
        { id: 3, type: 'issue', message: 'Maintenance request for Room 310', time: '1 hour ago', status: 'warning' },
        { id: 4, type: 'checkout', message: 'Guest checked out from Room 205', time: '2 hours ago', status: 'info' }
      ]);

      setAlerts([
        { id: 1, type: 'urgent', message: 'Low inventory: Towels below threshold', priority: 'high' },
        { id: 2, type: 'info', message: 'Staff schedule pending approval', priority: 'medium' },
        { id: 3, type: 'reminder', message: 'Monthly report due in 2 days', priority: 'low' }
      ]);

      setHotelData({
        hotelId: null,
        hotelName: 'Grand Plaza Hotel',
        location: 'Downtown, City Center',
        rating: 4.5
      });
    } finally {
      setLoading(false);
    }
  };

  const loadBookings = async () => {
    try {
      setBookingsLoading(true);
      setBookingsError('');

      const response = await getBookings({ limit: 50 });
      const list = response?.data?.bookings || response?.bookings || [];
      setBookings(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Error loading bookings:', error);
      setBookingsError(error?.response?.data?.message || 'Failed to load bookings');
      setBookings([]);
    } finally {
      setBookingsLoading(false);
    }
  };

  const normalizeBookingStatus = (booking) => {
    return (booking?.status || booking?.booking_status || '').toString().toLowerCase();
  };

  const handleBookingAction = async (bookingId, action) => {
    try {
      setBookingActionBusyId(bookingId);

      if (action === 'checkin') {
        await checkInBooking(bookingId);
      } else if (action === 'checkout') {
        await checkOutBooking(bookingId);
      } else if (action === 'cancel') {
        const reason = window.prompt('Cancellation reason (optional):') || '';
        await cancelBookingLifecycle(bookingId, reason);
      }

      await loadBookings();
    } catch (error) {
      console.error(`Booking ${action} failed:`, error);
      alert(error?.response?.data?.message || `Failed to ${action} booking`);
    } finally {
      setBookingActionBusyId(null);
    }
  };

  // ── Notification helpers ───────────────────────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await getNotifications();
      const data = res.data?.data || {};
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount ?? 0);
    } catch (e) {
      // silent – don't break dashboard if notifications fail
    }
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    fetchNotifications();
    const t = setInterval(fetchNotifications, 30_000);
    return () => clearInterval(t);
  }, [currentUser, fetchNotifications]);

  // Close notification panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifPanelRef.current && !notifPanelRef.current.contains(e.target)) {
        setShowNotifPanel(false);
      }
    };
    if (showNotifPanel) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifPanel]);

  const handleBellClick = async () => {
    const opening = !showNotifPanel;
    setShowNotifPanel(opening);
    if (opening && unreadCount > 0) {
      try {
        await markAllNotificationsRead();
        setUnreadCount(0);
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      } catch (e) { /* silent */ }
    }
  };

  const renderBookingsManagement = () => {
    if (bookingsLoading) {
      return <div>Loading bookings...</div>;
    }

    return (
      <div style={{ display: 'grid', gap: '12px' }}>
        {bookingsError && (
          <div style={{ color: '#b91c1c', background: '#fee2e2', padding: '10px', borderRadius: '8px' }}>
            {bookingsError}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Recent Bookings</h3>
          <button
            onClick={loadBookings}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              background: '#fff',
              cursor: 'pointer'
            }}
          >
            Refresh
          </button>
        </div>

        {bookings.length === 0 ? (
          <div style={{ color: '#6b7280' }}>No bookings found.</div>
        ) : (
          bookings.map((booking) => {
            const bookingId = booking.booking_id || booking.id;
            const status = normalizeBookingStatus(booking);
            const busy = bookingActionBusyId === bookingId;

            return (
              <div
                key={bookingId}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  padding: '12px',
                  display: 'grid',
                  gap: '8px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>Booking #{bookingId}</strong>
                  <span style={{ textTransform: 'capitalize', color: '#374151' }}>{status || 'unknown'}</span>
                </div>

                <div style={{ color: '#4b5563', fontSize: '14px' }}>
                  {booking.full_name || booking.guest_name || 'Guest'} • {booking.hotel_name || 'Hotel'}
                </div>

                <div style={{ color: '#6b7280', fontSize: '13px' }}>
                  {booking.check_in || booking.check_in_date || booking.checkin_date || '-'} → {booking.check_out || booking.check_out_date || booking.checkout_date || '-'}
                </div>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {status === 'confirmed' && (
                    <>
                      <button
                        disabled={busy}
                        onClick={() => handleBookingAction(bookingId, 'checkin')}
                        style={{ padding: '6px 10px', borderRadius: '6px', border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer' }}
                      >
                        Check In
                      </button>
                      <button
                        disabled={busy}
                        onClick={() => handleBookingAction(bookingId, 'cancel')}
                        style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ef4444', background: '#fff', color: '#ef4444', cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                    </>
                  )}

                  {status === 'checked_in' && (
                    <button
                      disabled={busy}
                      onClick={() => handleBookingAction(bookingId, 'checkout')}
                      style={{ padding: '6px 10px', borderRadius: '6px', border: 'none', background: '#16a34a', color: '#fff', cursor: 'pointer' }}
                    >
                      Check Out
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, trend, color = 'blue' }) => (
    <div className={`stat-card stat-card-${color}`}>
      <div className="stat-icon">
        <Icon size={24} />
      </div>
      <div className="stat-content">
        <h3>{title}</h3>
        <div className="stat-value">{value}</div>
        {subtitle && <p className="stat-subtitle">{subtitle}</p>}
        {trend && (
          <div className={`stat-trend ${trend > 0 ? 'positive' : 'negative'}`}>
            <TrendingUp size={16} />
            <span>{Math.abs(trend)}% vs last period</span>
          </div>
        )}
      </div>
    </div>
  );

  const QuickAction = ({ icon: Icon, label, onClick, color = 'primary' }) => (
    <button className={`quick-action quick-action-${color}`} onClick={onClick}>
      <Icon size={20} />
      <span>{label}</span>
    </button>
  );

  const renderOverview = () => (
    <div className="overview-section">
      {/* Hotel Header */}
      <div className="hotel-header">
        <div className="hotel-info">
          <Building2 size={32} />
          <div>
            <h2>{hotelData?.hotelName}</h2>
            <p>{hotelData?.location}</p>
          </div>
        </div>
        <button className="btn-ai-agent" onClick={() => setShowAgentChat(true)}>
          <Bot size={20} />
          Ask Manager AI
        </button>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="alerts-section">
          <h3>Important Alerts</h3>
          <div className="alerts-list">
            {alerts.map(alert => (
              <div key={alert.id} className={`alert alert-${alert.priority}`}>
                <AlertCircle size={18} />
                <span>{alert.message}</span>
                <button className="alert-dismiss">×</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        <StatCard
          icon={Bed}
          title="Room Occupancy"
          value={`${stats.occupiedRooms}/${stats.totalRooms}`}
          subtitle={`${stats.occupancyRate}% occupied`}
          trend={5}
          color="blue"
        />
        <StatCard
          icon={Calendar}
          title="Today's Activity"
          value={`${stats.checkInsToday} / ${stats.checkOutsToday}`}
          subtitle="Check-ins / Check-outs"
          color="green"
        />
        <StatCard
          icon={DollarSign}
          title="Revenue Today"
          value={`$${stats.revenue.today.toLocaleString()}`}
          subtitle={`$${stats.revenue.month.toLocaleString()} this month`}
          trend={12}
          color="purple"
        />
        <StatCard
          icon={Users}
          title="Active Staff"
          value={stats.activeStaff}
          subtitle={`${stats.pendingIssues} pending issues`}
          color="orange"
        />
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-section">
        <h3>Quick Actions</h3>
        <div className="quick-actions-grid">
          <QuickAction
            icon={Calendar}
            label="View Bookings"
            onClick={() => setActiveView('bookings')}
          />
          <QuickAction
            icon={Bed}
            label="Room Status"
            onClick={() => setActiveView('rooms')}
          />
          <QuickAction
            icon={UserCog}
            label="Staff Management"
            onClick={() => setActiveView('staff')}
          />
          <QuickAction
            icon={Package}
            label="Inventory"
            onClick={() => setActiveView('inventory')}
          />
          <QuickAction
            icon={BarChart3}
            label="Analytics"
            onClick={() => setActiveView('analytics')}
          />
          <QuickAction
            icon={FileText}
            label="Reports"
            onClick={() => setActiveView('reports')}
          />
        </div>
      </div>

      {/* Recent Activity */}
      <div className="recent-activity-section">
        <h3>Recent Activity</h3>
        <div className="activity-list">
          {recentActivities.map(activity => (
            <div key={activity.id} className={`activity-item activity-${activity.status}`}>
              <div className="activity-icon">
                {activity.status === 'success' && <CheckCircle size={20} />}
                {activity.status === 'warning' && <AlertCircle size={20} />}
                {activity.status === 'info' && <Clock size={20} />}
              </div>
              <div className="activity-content">
                <p>{activity.message}</p>
                <span className="activity-time">{activity.time}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="loading-container">
        <Activity className="spinner" size={48} />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="manager-dashboard">

      {/* ── TOP NAVBAR ── */}
      <header className="mgr-topnav">
        <div className="mgr-topnav-brand">
          <Building2 size={22} />
          <span>Manager Portal</span>
        </div>

        <nav className="mgr-topnav-tabs">
          {NAV_ITEMS.map(({ key, label, Icon, route }) => (
            <button
              key={key}
              className={`mgr-tab${activeView === key ? ' mgr-tab-active' : ''}`}
              onClick={() => {
                if (route) { navigate(route); } else { setActiveView(key); }
              }}
            >
              <Icon size={17} />
              <span>{label}</span>
            </button>
          ))}
        </nav>

        <div className="mgr-topnav-right">
          {/* ── Live Notification Bell ── */}
          <div ref={notifPanelRef} style={{ position: 'relative' }}>
            <button
              className="mgr-icon-btn"
              title="Notifications"
              onClick={handleBellClick}
              style={{ position: 'relative' }}
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span style={{
                  position: 'absolute', top: 2, right: 2,
                  background: '#ef4444', color: '#fff',
                  borderRadius: '50%', fontSize: 10,
                  minWidth: 16, height: 16,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, lineHeight: 1,
                }}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifPanel && (
              <div style={{
                position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                width: 340, maxHeight: 420, overflowY: 'auto',
                background: '#fff', borderRadius: 12,
                boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
                zIndex: 9999, padding: '6px 0',
                border: '1px solid #e5e7eb',
              }}>
                <div style={{
                  padding: '10px 16px 8px', borderBottom: '1px solid #f3f4f6',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>Notifications</span>
                  {unreadCount > 0 && (
                    <button
                      style={{ fontSize: 12, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer' }}
                      onClick={async () => {
                        try {
                          await markAllNotificationsRead();
                          setUnreadCount(0);
                          setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
                        } catch (e) { /* silent */ }
                      }}
                    >Mark all read</button>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#9ca3af', padding: '20px 16px', fontSize: 13 }}>
                    No notifications
                  </p>
                ) : (
                  notifications.map((n, idx) => (
                    <div key={n.notification_id ?? idx} style={{
                      padding: '10px 16px',
                      borderBottom: '1px solid #f9fafb',
                      background: n.is_read ? '#fff' : '#eff6ff',
                      cursor: 'default',
                    }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{n.title}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{n.body}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                        {new Date(n.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          <button
            className="mgr-icon-btn"
            title="Ask Manager AI"
            onClick={() => setShowAgentChat(true)}
          >
            <Bot size={20} />
          </button>
          <div className="mgr-avatar" title={userProfile?.name || 'Manager'}>
            {(userProfile?.name || 'M')[0].toUpperCase()}
          </div>
          <button
            className="mgr-icon-btn mgr-logout-btn"
            title="Log out"
            onClick={async () => { await logout(); navigate('/login', { replace: true }); }}
          >
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {/* ── PAGE TITLE BAR ── */}
      <div className="mgr-page-title">
        {NAV_ITEMS.find(n => n.key === activeView)?.label || 'Dashboard'}
      </div>

      {/* ── MAIN CONTENT ── */}
      <div className="mgr-content-area">
        {activeView === 'overview' && (
          <>
            <HotelOverview />
            {renderOverview()}
          </>
        )}
        {activeView === 'config' && <HotelConfiguration />}
        {activeView === 'bookings' && renderBookingsManagement()}
        {activeView === 'rooms' && (
          <div className="mgr-placeholder">
            <Bed size={56} className="mgr-placeholder-icon" />
            <h2>Room Management</h2>
            <p>Full room inventory, housekeeping, maintenance, pricing &amp; availability.</p>
            <button className="mgr-open-module-btn" onClick={() => navigate('/manager/rooms')}>Open Room Module</button>
          </div>
        )}
        {activeView === 'staff' && (
          <div className="mgr-placeholder">
            <Users size={56} className="mgr-placeholder-icon" />
            <h2>Staff Management</h2>
            <p>Attendance, leave, shifts, departments, payroll &amp; performance.</p>
            <button className="mgr-open-module-btn" onClick={() => navigate('/manager/staff')}>Open Staff Module</button>
          </div>
        )}
        {activeView === 'inventory' && <div className="mgr-coming-soon"><Package size={48}/><p>Inventory — Coming Soon</p></div>}
        {activeView === 'analytics' && <div className="mgr-coming-soon"><BarChart3 size={48}/><p>Analytics — Coming Soon</p></div>}
        {activeView === 'reports'   && <div className="mgr-coming-soon"><FileText size={48}/><p>Reports — Coming Soon</p></div>}
        {activeView === 'settings'  && <div className="mgr-coming-soon"><Settings size={48}/><p>Settings — Coming Soon</p></div>}
        {activeView === 'iot'       && <div className="mgr-coming-soon"><Cpu size={48}/><p>IoT Devices — Opening…</p></div>}
      </div>

      {/* AI Agent Chat */}
      {showAgentChat && (
        <div className="ai-agent-overlay">
          <ManagerAgent onClose={() => setShowAgentChat(false)} hotelId={hotelData?.hotelId} />
        </div>
      )}
      {!showAgentChat && (
        <button className="floating-ai-btn" onClick={() => setShowAgentChat(true)} title="Chat with Manager AI">
          <Bot size={26} />
        </button>
      )}
    </div>
  );
};

export default ManagerDashboard;
